/**
 * The start-point picker (`.picker`): a Leaflet map on the IGN base layer
 * (`components/map/basemap`), centred on the commune's centroid, where a tap
 * places (or moves) a marker.
 *
 * Leaflet's default marker icon resolves image URLs relative to the page,
 * which breaks under Vite's bundling unless the asset paths are configured
 * by hand; a charter-styled `L.divIcon` (a violet-ringed yellow circle, like
 * the prototype's `.cible`) sidesteps that entirely and looks right besides.
 */
import * as L from 'leaflet';
import { useEffect, useRef } from 'react';
import 'leaflet/dist/leaflet.css';

import { addBasemap } from '../../../components/map/basemap';

import { t } from '../../../i18n/fr';

import type { GeoPoint } from '../../../api/sessions';

export interface StartPointPickerProps {
  /** Map centre on first render — the commune's centroid. */
  center: GeoPoint;
  /** The currently chosen point, or `null` before anything is picked. */
  point: GeoPoint | null;
  /** Called with the tapped point. */
  onSelect: (point: GeoPoint) => void;
}

/** A charter-styled marker: violet ring, yellow fill, no external image asset. */
function markerIcon(): L.DivIcon {
  return L.divIcon({
    className: 'session-picker__marker-wrapper',
    html: '<span class="session-picker__marker">✚</span>',
    iconSize: [34, 34],
    iconAnchor: [17, 17],
  });
}

/**
 * Render the Leaflet start-point picker.
 *
 * @param props - Map centre, the current point and the select callback.
 * @returns The map container element.
 */
export function StartPointPicker({ center, point, onSelect }: StartPointPickerProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  // Mount the map once. Re-centring on `center`/`point` changes is handled
  // by the effect below, not by tearing this one down and rebuilding it.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) {
      return undefined;
    }
    const map = L.map(containerRef.current, {
      center: [center.latitude, center.longitude],
      zoom: 16,
    });
    addBasemap(map);
    map.on('click', (event: L.LeafletMouseEvent) => {
      onSelectRef.current({ longitude: event.latlng.lng, latitude: event.latlng.lat });
    });
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount once
  }, []);

  // Keep the marker (and the view) in sync with the chosen point.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) {
      return;
    }
    const target = point ?? center;
    const latlng: L.LatLngExpression = [target.latitude, target.longitude];
    if (!markerRef.current) {
      markerRef.current = L.marker(latlng, { icon: markerIcon() }).addTo(map);
    } else {
      markerRef.current.setLatLng(latlng);
    }
    map.panTo(latlng);
  }, [point, center]);

  return (
    <div
      ref={containerRef}
      className="picker"
      role="application"
      aria-label={t('session.open.mapLabel')}
    />
  );
}
